import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const ConfirmCtx = createContext(() => Promise.resolve(false));
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [typed, setTyped] = useState("");
  const resolver = useRef(null);

  const confirm = useCallback((opts) => {
    setTyped("");
    setState({
      title: "Are you sure?",
      description: "",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "danger",
      requireText: null,
      ...opts,
    });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result) => { setState(null); setTyped(""); if (resolver.current) { resolver.current(result); resolver.current = null; } };

  const needsType = !!state?.requireText;
  const matched = !needsType || typed.trim() === String(state.requireText).trim();
  const danger = state?.tone === "danger";

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => !o && close(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2">
              <span className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: danger ? "#FDE7E7" : "#E3F1EC" }}>
                <AlertTriangle className="h-5 w-5" style={{ color: danger ? "#B91C1C" : "var(--sj-primary)" }} />
              </span>
              {state?.title}
            </DialogTitle>
          </DialogHeader>
          {state?.description && <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>{state.description}</p>}
          {needsType && (
            <div className="mt-1">
              <label className="text-xs font-medium" style={{ color: "var(--sj-text-3)" }}>Type <b style={{ color: "var(--sj-text-2)" }}>{state.requireText}</b> to confirm</label>
              <input data-testid="confirm-type-input" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)}
                className="w-full mt-1 bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#0B5E4E]" />
            </div>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <button data-testid="confirm-cancel-btn" onClick={() => close(false)} className="rounded-full px-5 py-2.5 border font-medium text-sm" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>{state?.cancelLabel}</button>
            <button data-testid="confirm-ok-btn" disabled={!matched} onClick={() => close(true)} className="rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-40" style={{ background: danger ? "#B91C1C" : "var(--sj-primary)" }}>{state?.confirmLabel}</button>
          </div>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}
